import { useRef, useEffect } from 'react';
import { IconPlus, IconX, IconLayoutCards, IconUsers, IconSun, IconMoon, IconAperture, IconDroplet } from '@tabler/icons-react';
import { Text, Input, ButtonGroup, ErrorMsg } from '@fragments';
import Popper from '@fragments/Popper';
import { CommunityAssetImageField, AssetCategory } from '@features/uploadAsset';

const REDIRECT_CHOICES = [
    { value: 'community', label: 'Redirecionar para a comunidade' },
    { value: 'link', label: 'Redirecionar para outro link' },
];

const THEME_CHOICES = [
    { value: 'light', label: 'Claro', icon: <IconSun size={14} /> },
    { value: 'dark', label: 'Escuro', icon: <IconMoon size={14} /> },
];

function ActionCard({ action, removeAction }) {
    return (
        <div className="fbrp-action-card">
            <div className="fbrp-action-card-header">
                <div className="fbrp-action-card-type">
                    <IconUsers size={14} />
                    <Text.span contrast={6}>Inscrever na comunidade</Text.span>
                </div>
                <button className="fbrp-q-delete" onClick={() => removeAction(action.id)}>
                    <IconX size={13} />
                </button>
            </div>
        </div>
    );
}

export default function FormConfig({
    communityId,
    formName, setFormName,
    formActive, setFormActive,
    redirectChoice, setRedirectChoice,
    allowMultipleAnswers, setAllowMultipleAnswers,
    returnUrl, setReturnUrl,
    banner, setBanner,
    theme, setTheme,
    actions, addAction, removeAction,
    formErrors, clearError,
    scrollSignal,
}) {
    const popperRef = useRef();
    const bodyRef = useRef(null);

    useEffect(() => {
        if (!scrollSignal || !bodyRef.current) return;
        const id = setTimeout(() => {
            const field = bodyRef.current?.querySelector('[data-field-error="true"]');
            if (!field) return;
            field.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            field.querySelector('input, [contenteditable="true"]')?.focus();
        }, 80);
        return () => clearTimeout(id);
    }, [scrollSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRedirectChoice = (choice) => {
        setRedirectChoice(choice);
        if (choice === 'community') {
            setReturnUrl(null);
            clearError?.(['form', 'returnUrl']);
        }
    };

    return (
        <div className="form-builder-right-panel">
            <div className="fbrp-header">
                <div className="fbrp-header-left">
                    <div className="fbrp-form-icon">
                        <IconLayoutCards size={15} />
                    </div>
                    <Text.span contrast={8} weight={600}>Formulário</Text.span>
                </div>
            </div>

            <div className="fbrp-body" ref={bodyRef}>
                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>GERAL</Text.span>
                    </div>

                    <div className="fbrp-field" data-field-error={formErrors?.formName ? 'true' : undefined}>
                        <Input
                            value={formName}
                            onInput={v => {
                                setFormName(v);
                                clearError?.(['form', 'formName']);
                            }}
                            label="Nome do formulário"
                            placeholder="Novo formulário"
                            fullWidth
                            error={!!formErrors?.formName}
                        />
                        <ErrorMsg>{formErrors?.formName}</ErrorMsg>
                    </div>

                    <Input.Switch label="Formulário ativo" value={formActive} onInput={val => setFormActive(val)} />
                    <Input.Switch label="Permitir múltiplas respostas" value={allowMultipleAnswers} onInput={val => setAllowMultipleAnswers(val)} />

                    <ErrorMsg>{formErrors?.steps}</ErrorMsg>
                </div>

                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>APARÊNCIA</Text.span>
                    </div>

                    <Text.h4
                        className="mt-2 mb-1"
                        contrast={6}
                        weight={400}
                        left={<IconAperture />}
                    >
                        Banner
                    </Text.h4>

                    <CommunityAssetImageField
                        communityId={communityId}
                        category={AssetCategory.FORM_BANNER}
                        value={banner}
                        onChange={setBanner}
                        aspectRatio={5 / 3}
                        title="Banner do formulário"
                        fullWidth
                    />

                    <Text.h4
                        className="mt-3 mb-1"
                        contrast={6}
                        weight={400}
                        left={<IconDroplet />}
                    >
                        Tema
                    </Text.h4>
                    <ButtonGroup
                        options={THEME_CHOICES}
                        value={theme}
                        onChange={setTheme}
                        size="small"
                    />
                </div>

                <div className="fbrp-section">
                    <div className="fbrp-section-label">
                        <Text.span contrast={4}>AÇÕES PÓS-ENVIO</Text.span>
                    </div>

                    <Input.Choices
                        value={redirectChoice}
                        onInput={handleRedirectChoice}
                        options={REDIRECT_CHOICES}
                    />

                    {redirectChoice === 'link' && (
                        <div className="fbrp-field" style={{ marginTop: 'var(--gap-1)' }} data-field-error={formErrors?.returnUrl ? 'true' : undefined}>
                            <Input
                                value={returnUrl ?? ''}
                                onInput={val => {
                                    setReturnUrl(val || null);
                                    clearError?.(['form', 'returnUrl']);
                                }}
                                label="URL de destino"
                                placeholder="https://..."
                                fullWidth
                                error={!!formErrors?.returnUrl}
                            />
                            <ErrorMsg>{formErrors?.returnUrl}</ErrorMsg>
                        </div>
                    )}

                    {actions.length > 0 && (
                        <div className="fbrp-action-cards" style={{ marginTop: 'var(--gap-1)' }}>
                            {actions.map(action => (
                                <ActionCard key={action.id} action={action} removeAction={removeAction} />
                            ))}
                        </div>
                    )}

                    <button
                        className="fbrp-add-btn"
                        style={{ marginTop: 'var(--gap-1)' }}
                        onClick={(e) => popperRef.current.open(
                            [{ id: 'subscribe_community', label: 'Inscrever na comunidade', icon: <IconUsers size={16} />, onClick: () => addAction('subscribe_community') }],
                            'top-start',
                            e.currentTarget,
                        )}
                    >
                        <IconPlus size={13} />
                        <Text.span contrast={5}>Disparar ação adicional</Text.span>
                    </button>
                </div>
            </div>

            <Popper controlRef={popperRef} />
        </div>
    );
}
